import { RefObject, useEffect } from 'react';
//
import { nextFocus } from './nav';

// ----------------------------------------------------------------------

type Options = {
  open: boolean;
  onClose: VoidFunction;
  header: RefObject<HTMLElement>;
  trigger: RefObject<HTMLButtonElement>;
  dialog: RefObject<HTMLDivElement>;
};

const FOCUSABLE = 'a[href], button:not([disabled])';

export default function useMenuDialog({ open, onClose, header, trigger, dialog }: Options) {
  useEffect(() => {
    const bar = header.current;
    const button = trigger.current;
    const panel = dialog.current;
    if (!open || !bar || !button || !panel) {
      return undefined;
    }

    const { body, documentElement: root } = document;
    const saved = { overflow: root.style.overflow, paddingRight: body.style.paddingRight };
    const gap = `${window.innerWidth - root.clientWidth}px`;
    root.style.overflow = 'hidden';
    body.style.paddingRight = gap;
    panel.style.paddingRight = gap;

    const outside = Array.from(bar.parentElement?.children ?? []).filter(
      (el) => el !== bar && !el.hasAttribute('inert')
    );
    outside.forEach((el) => el.setAttribute('inert', ''));

    let keyboard = button.matches(':focus-visible');

    const start = keyboard ? panel.querySelector<HTMLElement>(FOCUSABLE) : panel;
    start?.focus({ preventScroll: true });

    const onPointerDown = () => {
      keyboard = false;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      keyboard = true;
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const items: HTMLElement[] = [
        button,
        ...Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)),
      ];
      const index = items.indexOf(document.activeElement as HTMLElement);
      event.preventDefault();
      items[nextFocus(index, items.length, event.shiftKey)].focus({ preventScroll: true });
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
      outside.forEach((el) => el.removeAttribute('inert'));
      root.style.overflow = saved.overflow;
      body.style.paddingRight = saved.paddingRight;
      if (keyboard) {
        button.focus({ preventScroll: true });
      }
    };
  }, [open, onClose, header, trigger, dialog]);
}
